import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { CuentaBeneficiario } from '../../interfaces/cuenta-beneficiario.interface';
import { AccountRecipientService } from '../../services/account-recipient.service';
import { BinanceDepositService } from '../../services/binance-deposit.service';
import {
  BalanceResponse,
  BinanceWithdrawalService,
} from '../../services/binance-withdrawal.service';
import { MainRatesService } from '../../services/main-rates.service';
import { ParameterService } from '../../services/parameter.service';
import { TramosVesUsdtResponse, WalletVesClientService } from '../../services/wallet-ves-client.service';
import { PAGO_MOVIL_MONTO_MAX_DEFAULT } from 'src/app/shared/enums/parameters.enum';
import AddAccountComponent from '../account-recipient/components/add-account/add-account.component';
import {
  UsdtDestinationData,
  UsdtDestinationModalComponent,
  UsdtDestinationResult,
} from '../calculator-cryto/components/usdt-destination-modal/usdt-destination-modal.component';
import {
  esEnvioIncierto,
  LiquidacionUsdtVes,
  liquidacionDeRespuesta,
  mensajeEnvioIncierto,
} from './convert-usdt-ves.rules';
import {
  ESTADO_TRAMOS_INICIAL,
  EstadoTramos,
  LecturaTramos,
  registrarLectura,
  tramosParaCotizar,
  tramosValidos,
} from '../convert-ves-usdt/convert-ves-usdt.tramos';
import { EscalonVista } from '../home-rates/models/home-rates.model';
import { escaleraDesde } from '../home-rates/rules/home-rates.rules';
import { avisarBajaDeTasaVenta, TasaVentaTramo, tasaVentaParaMontoUsdt } from './convert-usdt-ves.tramos';
import { esRechazoPorTasa } from 'src/app/shared/utils/error-http-real';
import { esTitularVesChileno, tieneSaldoVes } from 'src/app/shared/utils/saldo-ves';

@Component({
  selector: 'app-convert-usdt-ves',
  templateUrl: './convert-usdt-ves.component.html',
  styleUrls: ['./convert-usdt-ves.component.sass'],
})
export class ConvertUsdtVesComponent implements OnInit, OnDestroy {
  // Step management
  currentStep: number = 1;
  totalSteps: number = 4;
  destination: 'wallet' | 'withdraw' | null = null;

  // Responsive modal
  isVertical = false;
  @HostListener('window:resize')
  onWindowResize() {
    this.isVertical = window.innerWidth <= 768;
  }

  // Configuración
  readonly MIN_USDT_AMOUNT = 10;

  // Estado del componente
  saldoUsdt: number = 0;
  saldoUsdtTotal: number = 0;
  saldoUsdtBloqueado: number = 0;
  saldoUsdtDisponible: number = 0;
  /** Saldo VES actual (VE y chilenos): se muestra al elegir "Guardar en Wallet VES". `null` = no se pudo leer. */
  saldoVes: number | null = null;
  tasaUsdtVes: number = 0;
  /**
   * Base de hoy (`rates/usdt-ves/rate`). `tasaUsdtVes` es la que se muestra,
   * calcula y envía: la del tramo del monto, o esta base si no hay tramos.
   */
  tasaBaseUsdtVes: number = 0;
  // Tramos USDT→VES: lo último bueno y los fallos seguidos (mismas reglas que
  // VES→USDT: un fallo suelto conserva los de antes; 3 seguidos o datos de
  // más de 5 minutos caen a la base).
  estadoTramos: EstadoTramos = ESTADO_TRAMOS_INICIAL;
  tramoVenta: TasaVentaTramo | null = null;
  loadingRate: boolean = true;
  rateError: string | null = null;
  montoAConvertir: number = 0;
  montoConvertido: number = 0;

  // Horario de operación
  horarioOperacion: string = '4:00 - 23:00';
  isOperatingHours: boolean = true;
  operatingHoursMessage: string =
    'No puedes operar en este momento. El horario de operación es de 4:00 a 23:00 (hora de Venezuela).';

  // Estados de UI
  loading: boolean = false;
  isConverting: boolean = false;
  isTransitioning: boolean = false;
  isModalOpen: boolean = false;
  slideDirection: 'left' | 'right' = 'left';
  errorMessage: string | null = null;
  showRateChangedWarning: boolean = false;
  previousRate: number = 0;

  // Beneficiarios
  beneficiaries: CuentaBeneficiario[] = [];
  selectedBeneficiary: CuentaBeneficiario | null = null;
  searchTerm: string = '';
  loadingBeneficiarios: boolean = false;
  // Límite máximo (Bs) para Pago Móvil, configurable desde admin (PAGO_MOVIL_MONTO_MAX).
  pagoMovilMontoMax: number = PAGO_MOVIL_MONTO_MAX_DEFAULT;

  // Usuario
  user: any;
  tipoId: string = '';
  subscription: Subscription = new Subscription();

  // Transaction timestamp
  transactionCreatedAt: Date | null = null;

  /**
   * VES y tasa que liquidó el servidor (respuesta del swap). La pantalla final
   * y el texto para compartir usan esto; `null` mientras no haya respuesta.
   */
  liquidacion: LiquidacionUsdtVes | null = null;

  /**
   * El swap falló sin respuesta clara: pudo haberse creado. La pantalla queda
   * cerrada (sin reintento) y sólo ofrece ir al historial.
   */
  envioIncierto: boolean = false;
  mensajeIncierto: string = '';

  // Anti-hackeo: bloqueo permanente tras envío exitoso
  operationCompleted: boolean = false;

  private timeCheckInterval: any;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private spinner: SpinnerService,
    private accountRecipientService: AccountRecipientService,
    private binanceWithdrawalService: BinanceWithdrawalService,
    private binanceDepositService: BinanceDepositService,
    private mainRatesService: MainRatesService,
    private walletVesClientService: WalletVesClientService,
    private parameterService: ParameterService,
    private dialog: MatDialog,
  ) {
    this.isVertical = window.innerWidth <= 768;
    this.subscription.add(
      this.store.select('auth').subscribe(async ({ user }: any) => {
        this.user = user;
        this.tipoId = user?.tipoId || '';

        if (this.user?.usuarioId) {
          await this.obtenerTasaUsdtVes();
          await this.obtenerSaldoUsdt();
          await this.obtenerBeneficiarios();
          this.obtenerSaldoVes();
        }
      }),
    );
  }

  ngOnInit(): void {
    this.checkOperatingHours();
    this.setupTimeMonitoring();
    this.obtenerTasaUsdtVes();
    this.cargarPagoMovilMontoMax();
  }

  async cargarPagoMovilMontoMax(): Promise<void> {
    this.pagoMovilMontoMax = await this.parameterService.getPagoMovilMontoMax();
  }

  /**
   * El beneficiario recibe VES (montoConvertido), por lo que el límite de
   * Pago Móvil (en Bs) se compara contra ese monto.
   */
  get isPagoMovilBlockedByAmount(): boolean {
    return this.montoConvertido > this.pagoMovilMontoMax;
  }

  /** Un beneficiario de Pago Móvil no se puede elegir si el monto supera el límite. */
  isBeneficiaryPagoMovilBlocked(beneficiary: CuentaBeneficiario): boolean {
    return beneficiary?.movilPay === 1 && this.isPagoMovilBlockedByAmount;
  }

  ngOnDestroy(): void {
    if (this.timeCheckInterval) {
      clearInterval(this.timeCheckInterval);
    }
    this.subscription.unsubscribe();
  }

  // ============================================================
  // MODAL DE DESTINO
  // ============================================================

  async openDestinationModal(): Promise<void> {
    if (
      this.isTransitioning ||
      this.isConverting ||
      this.operationCompleted ||
      this.isModalOpen ||
      this.isLoadingButton
    )
      return;
    if (!this.isValidAmount) return;

    this.isModalOpen = true;
    this.isLoadingButton = true;

    // Refrescar tasa antes de continuar y asegurar recálculo
    try {
      await this.refrescarTasaYRecalcular();
    } finally {
      this.isLoadingButton = false;
    }
    this.montoConvertido = this.montoAConvertir * this.tasaUsdtVes;

    const dialogData: UsdtDestinationData = {
      clpAmount: this.montoAConvertir,
      usdtAmount: this.montoConvertido,
      rate: this.tasaUsdtVes,
      sendFormatted: this.formatUSDTWithSymbol(this.montoAConvertir),
      receiveFormatted: this.formatVES(this.montoConvertido),
      title: '¿Cómo deseas recibir tus VES?',
      subtitle: 'Elige qué hacer con los bolívares que recibirás',
      walletTitle: 'Guardar en Wallet VES',
      // VE y chilenos con saldo VES: los dos pueden enviarlos después. Al
      // chileno se le dicen sus salidas (Venezuela, pesos a su cuenta, USDT).
      walletDescription:
        (esTitularVesChileno(this.tipoId)
          ? 'Tus VES quedarán en tu saldo VES de Aguacate. Desde ahí podrás enviarlos a Venezuela, pasarlos a pesos a tu cuenta en Chile o convertirlos a USDT.'
          : 'Tus VES quedarán disponibles en tu wallet Aguacate. Podrás enviarlos a un beneficiario cuando quieras.') +
        (this.saldoVes !== null ? ` Saldo VES actual: ${this.formatVES(this.saldoVes)}.` : ''),
      walletBenefits: [
        'Disponible al instante',
        'Envía cuando quieras',
        'Sin comisiones adicionales',
      ],
      withdrawTitle: 'Enviar a beneficiario bancario',
      withdrawDescription:
        'Envía los VES directamente a una cuenta bancaria venezolana. Deberás seleccionar un beneficiario en el siguiente paso.',
      withdrawWarning: 'Requiere beneficiario bancario registrado',
    };

    const dialogRef = this.dialog.open(UsdtDestinationModalComponent, {
      data: dialogData,
      panelClass: 'usdt-destination-dialog',
      maxWidth: '100vw',
      width: this.isVertical ? '100vw' : '520px',
      height: 'auto',
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .subscribe(async (result: UsdtDestinationResult | null) => {
        if (!result) {
          this.isModalOpen = false;
          return;
        }

        this.destination = result.destination;

        if (result.destination === 'wallet') {
          // Wallet path: saltar beneficiario, ir directo a confirmación
          this.selectedBeneficiary = null;
          this.goToStep(2);
          this.isModalOpen = false;
        } else {
          // Beneficiary path: ir a selección de beneficiario
          this.loadingBeneficiarios = true;
          this.goToStep(2);
          this.isModalOpen = false;
          await this.cargarBeneficiarios();
        }
      });
  }

  private async cargarBeneficiarios(): Promise<void> {
    try {
      await this.obtenerBeneficiarios();
    } finally {
      this.loadingBeneficiarios = false;
    }
  }

  // ============================================================
  // STEP NAVIGATION
  // ============================================================

  goToStep(target: 1 | 2 | 3 | 4): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.slideDirection = target > this.currentStep ? 'left' : 'right';
    setTimeout(() => {
      this.currentStep = target;
      this.isTransitioning = false;
    }, 300);
  }

  async nextStep(): Promise<void> {
    if (this.isTransitioning || this.isConverting || this.operationCompleted)
      return;
    if (!this.validateCurrentStep()) return;

    this.isTransitioning = true;

    // Step 2 wallet path: confirmar directamente
    if (this.currentStep === 2 && this.destination === 'wallet') {
      await this.realizarConversion();
      return;
    }

    // Step 2 beneficiary path: ir a step 3 (confirmación con beneficiario)
    if (this.currentStep === 2 && this.destination === 'withdraw') {
      await this.refrescarTasaYRecalcular();
      this.slideDirection = 'left';
      setTimeout(() => {
        this.currentStep = 3;
        this.isTransitioning = false;
      }, 300);
      return;
    }

    // Step 3: confirmar operación con beneficiario
    if (this.currentStep === 3) {
      await this.realizarConversion();
      return;
    }

    this.slideDirection = 'left';
    setTimeout(() => {
      this.currentStep++;
      this.isTransitioning = false;
    }, 300);
  }

  previousStep(): void {
    if (this.isTransitioning || this.envioIncierto) return;

    if (this.currentStep === 2) {
      // Volver al step 1 y resetear destino
      this.destination = null;
      this.selectedBeneficiary = null;
      this.goToStep(1);
      return;
    }

    if (this.currentStep === 3) {
      // Volver a selección de beneficiario
      this.goToStep(2);
      return;
    }

    if (this.currentStep > 1) {
      this.slideDirection = 'right';
      this.isTransitioning = true;
      setTimeout(() => {
        this.currentStep--;
        this.isTransitioning = false;
      }, 300);
    } else {
      this.navigateBack();
    }
  }

  validateCurrentStep(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.isValidAmount && !this.errorMessage && !this.isConverting;
      case 2:
        if (this.destination === 'wallet') return true;
        // No avanzar con un beneficiario Pago Móvil si el monto supera el límite
        if (
          this.selectedBeneficiary &&
          this.isBeneficiaryPagoMovilBlocked(this.selectedBeneficiary)
        ) {
          return false;
        }
        return this.selectedBeneficiary !== null;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  }

  canProceed(): boolean {
    return this.validateCurrentStep();
  }

  // ============================================================
  // RATE & OPERATING HOURS
  // ============================================================

  // ── Tramos USDT→VES ──

  /** Distingue "falló" de "llegó vacío". Nunca lanza. */
  private async obtenerTramosVenta(): Promise<LecturaTramos> {
    try {
      const data = await firstValueFrom(this.walletVesClientService.getTramosUsdtVes());
      return { ok: true, data: data ?? null };
    } catch {
      return { ok: false };
    }
  }

  /** Tasa del tramo para el monto actual, con la base y los tramos guardados. */
  private tasaDelMonto(): number {
    if (!(this.tasaBaseUsdtVes > 0)) return this.tasaUsdtVes;
    this.tramoVenta = tasaVentaParaMontoUsdt(
      tramosParaCotizar(this.estadoTramos, Date.now()),
      this.montoAConvertir,
      this.tasaBaseUsdtVes,
    );
    return this.tramoVenta.tasa;
  }

  /**
   * Guarda la base nueva, vuelve a leer los tramos (si fallan quedan los de
   * antes) y devuelve la tasa efectiva para el monto actual.
   */
  private async tasaEfectivaDesde(base: number): Promise<number> {
    this.tasaBaseUsdtVes = Number(base) || 0;
    this.estadoTramos = registrarLectura(this.estadoTramos, await this.obtenerTramosVenta(), Date.now());
    return this.tasaDelMonto();
  }

  /** VES de más por el tramo del monto (0 si no hay tramo que mejore la base). */
  get extraVesTramo(): number {
    return this.tramoVenta?.extraVes ?? 0;
  }

  get letraTramoVenta(): string {
    return this.tramoVenta?.letra ?? '';
  }

  // ── Escalera de tramos (presentación; la tasa y el envío no cambian) ──

  private escaleraCache: { data: TramosVesUsdtResponse | null; esc: EscalonVista[] | null } | null = null;

  /**
   * Los tramos con los que se cotiza, en la forma de `app-escalera-tramos`.
   * Null (la pantalla queda como antes) sin tramos, con uno solo, si todos
   * pagan lo mismo o si están desactualizados.
   */
  get escaleraUsdtVes(): EscalonVista[] | null {
    const data = tramosParaCotizar(this.estadoTramos, Date.now());
    if (!this.escaleraCache || this.escaleraCache.data !== data) {
      const esc = data
        ? escaleraDesde(
            tramosValidos(data).map((t) => ({ desde: Number(t.desde), tasa: Number(t.tasa) })),
            { unidadCorte: 'USDT', monedaMonto: 'USDT', tipoAhorro: 'RECIBE_MAS', exigirMejora: true },
          )
        : null;
      this.escaleraCache = { data, esc };
    }
    return this.escaleraCache.esc;
  }

  /** «Usar X» de la escalera: escribe los USDT en el campo, como si los tecleara. */
  usarMontoUsdt(monto: number): void {
    if (!this.isOperatingHours || !(monto > 0)) return;
    this.montoAConvertir = monto;
    this.calcularConversion();
  }

  private async refrescarTasaYRecalcular(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.mainRatesService.getUsdtVesRateForUser(),
      );

      if (response.success && response.data) {
        const nuevaTasa = await this.tasaEfectivaDesde(response.data.tasaFinal);

        if (nuevaTasa !== this.tasaUsdtVes) {
          const anterior = this.tasaUsdtVes;
          this.tasaUsdtVes = nuevaTasa;
          this.montoConvertido = this.montoAConvertir * this.tasaUsdtVes;
          // Se avisa solo si empeoró para el cliente (menos VES por USDT),
          // como en VES→USDT. Si mejoró, se actualiza en silencio.
          if (avisarBajaDeTasaVenta(anterior, nuevaTasa)) {
            this.previousRate = anterior;
            this.mostrarWarningTasaCambiada();
          }
        }
      }
    } catch (error) {}
  }

  private mostrarWarningTasaCambiada(): void {
    this.showRateChangedWarning = true;
    setTimeout(() => {
      this.showRateChangedWarning = false;
    }, 3000);
  }

  private checkOperatingHours(): void {
    const now = new Date();
    const venezuelaTime = new Date(
      now.toLocaleString('en-US', { timeZone: 'America/Caracas' }),
    );
    const currentHour = venezuelaTime.getHours();

    this.isOperatingHours = currentHour >= 4 && currentHour < 23;

    if (!this.isOperatingHours) {
      this.resetConversionValues();
      this.errorMessage = this.operatingHoursMessage;
    } else if (this.errorMessage === this.operatingHoursMessage) {
      this.errorMessage = null;
    }
  }

  private setupTimeMonitoring(): void {
    this.timeCheckInterval = setInterval(() => {
      this.checkOperatingHours();
    }, 60000);
  }

  // ============================================================
  // CALCULATOR
  // ============================================================

  calcularConversion(): void {
    if (!this.isOperatingHours) {
      this.montoConvertido = 0;
      this.errorMessage = this.operatingHoursMessage;
      return;
    }

    // Tasa del tramo del monto (sin tramos, la base de siempre).
    this.tasaUsdtVes = this.tasaDelMonto();

    if (!this.montoAConvertir || !this.tasaUsdtVes) {
      this.montoConvertido = 0;
      this.errorMessage = null;
      return;
    }

    this.montoConvertido = this.montoAConvertir * this.tasaUsdtVes;

    if (this.saldoUsdt === 0) {
      this.errorMessage = null;
    } else if (this.montoAConvertir < this.MIN_USDT_AMOUNT) {
      this.errorMessage = `El monto mínimo es ${this.MIN_USDT_AMOUNT} USDT`;
    } else if (this.montoAConvertir > this.saldoUsdt) {
      this.errorMessage = 'El monto excede tu saldo disponible';
    } else {
      this.errorMessage = null;
    }
  }

  usarMaximo(): void {
    if (!this.isOperatingHours) return;
    this.montoAConvertir = this.saldoUsdt;
    this.calcularConversion();
  }

  get tienesSaldo(): boolean {
    return this.saldoUsdt > 0;
  }

  get isValidAmount(): boolean {
    return (
      this.isOperatingHours &&
      this.tasaUsdtVes > 0 &&
      !this.loadingRate &&
      this.saldoUsdt >= this.MIN_USDT_AMOUNT &&
      this.montoAConvertir >= this.MIN_USDT_AMOUNT &&
      this.montoAConvertir <= this.saldoUsdt
    );
  }

  get showConvertirButton(): boolean {
    return (
      this.tienesSaldo &&
      this.isValidAmount &&
      !this.errorMessage &&
      !this.isConverting
    );
  }

  get showRecargarButton(): boolean {
    return !this.tienesSaldo;
  }

  get buttonText(): string {
    if (this.isConverting || this.isLoadingButton) return 'Procesando...';
    if (!this.tienesSaldo) return 'Recargar USDT';
    return 'Continuar';
  }

  get isButtonDisabled(): boolean {
    if (this.isLoadingButton) return true;
    if (!this.tienesSaldo) return false;
    return !this.showConvertirButton;
  }

  // Flag para loading del botón principal (paso 1)
  isLoadingButton: boolean = false;

  onButtonClick(): void {
    if (this.isTransitioning || this.isModalOpen || this.isLoadingButton)
      return;
    if (!this.tienesSaldo) {
      this.navigateToRecharge();
      return;
    }
    if (!this.isValidAmount) return;

    if (tieneSaldoVes(this.tipoId, this.user?.numeroId)) {
      // VE y chilenos con saldo VES: modal para elegir wallet o beneficiario
      this.openDestinationModal();
    } else {
      // Otros: flujo directo a beneficiario
      this.destination = 'withdraw';
      this.loadingBeneficiarios = true;
      this.goToStep(2);
      this.cargarBeneficiarios();
    }
  }

  // ============================================================
  // BENEFICIARY METHODS
  // ============================================================

  get filteredBeneficiaries(): CuentaBeneficiario[] {
    if (!this.searchTerm) {
      return this.beneficiaries;
    }
    return this.beneficiaries.filter(
      (b) =>
        b.nombre.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        b.apellidos.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        b.banco?.nombre.toLowerCase().includes(this.searchTerm.toLowerCase()),
    );
  }

  selectBeneficiary(beneficiario: CuentaBeneficiario): void {
    // Al intentar seleccionar un Pago Móvil con monto sobre el límite: avisar y no seleccionar
    if (this.isBeneficiaryPagoMovilBlocked(beneficiario)) {
      Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'warning',
        title: 'Pago Móvil no disponible',
        text: `Pago Móvil no está disponible para montos superiores a ${this.formatVESNumber(
          this.pagoMovilMontoMax,
        )} Bs. Selecciona un beneficiario de otro tipo.`,
        confirmButtonText: 'Entendido',
      });
      return;
    }
    this.selectedBeneficiary = beneficiario;
  }

  openAddBeneficiaryDialog(): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(AddAccountComponent, {
      data: {
        type: 'CREATE',
        cuenta: null,
        isVeUser: this.user?.tipoId === 'VE',
        // Monto en Bs de la operación: el beneficiario recibe VES (montoConvertido)
        montoBs: this.montoConvertido,
      },
      disableClose: true,
      panelClass: 'aguacate-dialog',
      maxWidth: isMobile ? '95vw' : '620px',
      minWidth: isMobile ? '90vw' : '560px',
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true && this.user?.usuarioId) {
        await this.obtenerBeneficiarios();
      }
    });
  }

  getSelectedBeneficiaryName(): string {
    if (!this.selectedBeneficiary) {
      return 'No seleccionado';
    }
    return `${this.selectedBeneficiary.nombre} ${this.selectedBeneficiary.apellidos}`;
  }

  getSelectedBeneficiaryBank(): string {
    if (!this.selectedBeneficiary || !this.selectedBeneficiary.banco) {
      return '';
    }
    return this.selectedBeneficiary.banco.nombre;
  }

  // ============================================================
  // CONVERSION
  // ============================================================

  async realizarConversion(): Promise<void> {
    if (!this.isValidAmount || this.isConverting || this.operationCompleted)
      return;
    if (this.destination === 'withdraw' && !this.selectedBeneficiary) return;
    // Defensa: no permitir Pago Móvil si el monto supera el límite
    if (
      this.destination === 'withdraw' &&
      this.selectedBeneficiary &&
      this.isBeneficiaryPagoMovilBlocked(this.selectedBeneficiary)
    )
      return;

    // Anti-manipulación: destino wallet solo para quien tiene saldo VES
    if (this.destination === 'wallet' && !tieneSaldoVes(this.tipoId, this.user?.numeroId)) {
      this.router.navigate(['/wallet/home']);
      return;
    }

    // Bloquear inmediatamente para evitar doble-click
    this.isConverting = true;
    this.showRateChangedWarning = false;

    // Refrescar tasa antes de confirmar
    try {
      const response = await firstValueFrom(
        this.mainRatesService.getUsdtVesRateForUser(),
      );

      if (response.success && response.data) {
        const nuevaTasa = await this.tasaEfectivaDesde(response.data.tasaFinal);

        if (nuevaTasa !== this.tasaUsdtVes) {
          this.previousRate = this.tasaUsdtVes;
          this.tasaUsdtVes = nuevaTasa;
          this.montoConvertido = this.montoAConvertir * this.tasaUsdtVes;
          this.showRateChangedWarning = true;
          // DETENER: desbloquear y dejar que el usuario vea los nuevos montos y re-confirme
          this.isConverting = false;
          this.isTransitioning = false;
          return;
        }
      }
    } catch (error) {}

    // Si la tasa no cambió, proceder con el swap
    let spinnerRef = this.spinner.start();

    try {
      let response: any;

      if (this.destination === 'wallet') {
        // VE wallet: nuevo endpoint wallet-ves-client/swap-usdt-ves
        response = await firstValueFrom(
          this.walletVesClientService.swapUsdtToVes({
            userId: Number(this.user.usuarioId),
            totalUsdt: this.montoAConvertir,
            totalVes: this.montoConvertido,
            tasa: this.tasaUsdtVes,
          }),
        );
      } else {
        // Beneficiario: endpoint existente binance-deposits/swap-usdt-to-ves
        response = await firstValueFrom(
          this.binanceDepositService.swapUsdtToVes({
            userId: String(this.user.usuarioId),
            usdtAmount: this.montoAConvertir,
            vesRate: this.tasaUsdtVes,
            vesAmount: this.montoConvertido,
            cuentaBeneficiarioId:
              this.selectedBeneficiary!.cuentaBeneficiarioID!,
          }),
        );
      }

      this.spinner.stop(spinnerRef);
      this.isConverting = false;
      this.isTransitioning = false;
      this.operationCompleted = true;

      const successMsg =
        response?.data?.message ||
        response?.message ||
        'Tu operación está siendo procesada.';

      if (response.success) {
        this.transactionCreatedAt = new Date();
        this.liquidacion = liquidacionDeRespuesta(this.destination, response, {
          ves: this.montoConvertido,
          tasa: this.tasaUsdtVes,
        });

        // Destino wallet: es una conversión al saldo VES propio, no un envío.
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'success',
          title: this.destination === 'wallet' ? 'Conversión realizada' : 'Transacción creada',
          text: this.destination === 'wallet' ? 'Los bolívares quedaron en tu saldo VES.' : successMsg,
          confirmButtonText: 'Entendido',
        });

        this.slideDirection = 'left';
        this.currentStep = 4;
      } else {
        this.operationCompleted = false;
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'error',
          title: 'Error',
          text: successMsg,
          confirmButtonText: 'Entendido',
        });
      }
    } catch (error: any) {
      this.spinner.stop(spinnerRef);
      this.isConverting = false;
      this.isTransitioning = false;

      // Sin respuesta clara (red, 5xx, error aplanado por el interceptor): la
      // operación pudo haberse creado. No se ofrece reintento.
      if (esEnvioIncierto(error)) {
        this.operationCompleted = true;
        this.envioIncierto = true;
        this.mensajeIncierto = mensajeEnvioIncierto(this.destination);
        this.slideDirection = 'left';
        this.currentStep = 4;
        return;
      }

      this.operationCompleted = false;

      // El back rechazó la tasa (400 "La tasa cambió mientras confirmabas…"):
      // no se creó nada. Se refresca la tasa para que el cliente vea los montos
      // nuevos y confirme de nuevo.
      if (esRechazoPorTasa(error?.status, error?.message)) {
        await this.refrescarTasaYRecalcular();
      }

      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Ocurrió un error al procesar la conversión.',
        confirmButtonText: 'Entendido',
      });
    }
  }

  // ============================================================
  // SHARE & FINISH
  // ============================================================

  async shareTransaction(): Promise<void> {
    if (!this.transactionCreatedAt) {
      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'error',
        title: 'Error',
        text: 'No se pueden compartir los datos de la transacción.',
      });
      return;
    }

    const transactionData = this.formatTransactionData();

    try {
      await navigator.clipboard.writeText(transactionData);

      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'success',
        title: 'Copiado',
        text: 'Los datos de la transacción han sido copiados al portapapeles.',
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (error) {
      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'error',
        title: 'Error',
        text: 'No se pudo copiar al portapapeles.',
      });
    }
  }

  formatTransactionData(): string {
    if (!this.transactionCreatedAt) {
      return '';
    }

    const date = new Date(this.transactionCreatedAt);
    const pad = (n: number) => n.toString().padStart(2, '0');

    const dateUTC = `${date.getUTCFullYear()}-${pad(
      date.getUTCMonth() + 1,
    )}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(
      date.getUTCMinutes(),
    )}:${pad(date.getUTCSeconds())}`;

    let data = `
    DATOS DE TRANSACCIÓN - USDT a VES

⏰ Hora de creación: ${dateUTC}

💰 Monto enviado: ${this.formatUSDT(this.montoAConvertir)} USDT

💵 Monto a recibir: ${this.formatVES(this.vesLiquidados)}

💱 Tasa de cambio: 1 USDT = ${this.formatVESNumber(this.tasaLiquidada)} VES`;

    if (this.destination === 'wallet') {
      data += `\n\n📍 Destino: Wallet VES Aguacate`;
    } else if (this.selectedBeneficiary) {
      const beneficiaryName = `${this.selectedBeneficiary.nombre} ${this.selectedBeneficiary.apellidos}`;
      const beneficiaryBank =
        this.selectedBeneficiary.banco?.nombre || 'No disponible';
      data += `\n\n👥 Beneficiario: ${beneficiaryName}\n\n🏦 Banco: ${beneficiaryBank}`;
    }

    return data.trim();
  }

  /** VES de la operación ya creada: lo del servidor, o lo cotizado si no vino. */
  get vesLiquidados(): number {
    return this.liquidacion?.ves ?? this.montoConvertido;
  }

  /** Tasa de la operación ya creada: la del servidor, o la cotizada si no vino. */
  get tasaLiquidada(): number {
    return this.liquidacion?.tasa ?? this.tasaUsdtVes;
  }

  finishTransaction(): void {
    this.router.navigate(['/wallet']);
  }

  /** Historial de movimientos USDT (el mismo al que manda el remittance-hub). */
  irAlHistorial(): void {
    this.router.navigate(['/wallet/client/load-pays-usdt']);
  }

  navigateToRecharge(): void {
    this.router.navigate(['/wallet/client/load-pays-usdt']);
  }

  navigateBack(): void {
    this.router.navigate(['/wallet/client/load-pays-usdt']);
  }

  resetConversionValues(): void {
    this.montoAConvertir = 0;
    this.montoConvertido = 0;
    this.errorMessage = null;
  }

  // ============================================================
  // SERVICE METHODS
  // ============================================================

  async obtenerTasaUsdtVes(): Promise<void> {
    this.loadingRate = true;
    this.rateError = null;

    try {
      const response = await firstValueFrom(
        this.mainRatesService.getUsdtVesRateForUser(),
      );

      if (response.success && response.data) {
        this.tasaUsdtVes = await this.tasaEfectivaDesde(response.data.tasaFinal);
      } else {
        this.rateError = response.message || 'No se pudo obtener la tasa';
      }
    } catch (error: any) {
      this.rateError = error?.message || 'Error al obtener la tasa de cambio';
    } finally {
      this.loadingRate = false;
    }
  }

  async obtenerSaldoUsdt(): Promise<void> {
    try {
      const userId = this.user?.usuarioId || localStorage.getItem('userId');
      if (userId) {
        const balanceObservable =
          await this.binanceWithdrawalService.getBalance();
        const balance: BalanceResponse =
          await firstValueFrom(balanceObservable);

        this.saldoUsdtTotal = balance.totalBalance || 0;
        this.saldoUsdtBloqueado = balance.blockedBalance || 0;
        this.saldoUsdtDisponible = balance.availableBalance || 0;
        this.saldoUsdt = balance.availableBalance || 0;
      }
    } catch (error) {
      this.saldoUsdtTotal = 0;
      this.saldoUsdtBloqueado = 0;
      this.saldoUsdtDisponible = 0;
      this.saldoUsdt = 0;
    }
  }

  async obtenerBeneficiarios(): Promise<void> {
    if (this.user?.usuarioId) {
      try {
        this.beneficiaries = await this.accountRecipientService.getByUser(
          this.user.usuarioId,
        );
      } catch (error) {
        this.beneficiaries = [];
      }
    }
  }

  /** Saldo VES para el destino wallet. Sin movimientos VES es 0. */
  obtenerSaldoVes(): void {
    if (!tieneSaldoVes(this.tipoId, this.user?.numeroId)) return;
    this.walletVesClientService.getMyBalance().subscribe({
      next: (res) => (this.saldoVes = Number(res?.data?.balanceVes) || 0),
      error: () => (this.saldoVes = null),
    });
  }

  // ============================================================
  // FORMAT METHODS
  // ============================================================

  formatVES(value: number): string {
    if (!value && value !== 0) return 'Bs. 0,00';
    const roundedValue = Math.round(value * 100) / 100;
    return `Bs. ${roundedValue.toLocaleString('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  formatVESNumber(value: number): string {
    if (!value && value !== 0) return '0,00';
    const roundedValue = Math.round(value * 100) / 100;
    return roundedValue.toLocaleString('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatUSDT(value: number): string {
    if (!value && value !== 0) return '0.00';
    const roundedValue = Math.round(value * 100) / 100;
    return roundedValue.toFixed(2);
  }

  formatUSDTWithSymbol(value: number): string {
    if (!value && value !== 0) return '$0.00 USDT';
    const roundedValue = Math.round(value * 100) / 100;
    return `$${roundedValue.toFixed(2)} USDT`;
  }
}
